import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveEstimateMultiPartMutation } from '~/api/easy-estimator-api/estimate-api/estimateService';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import {
  DEFAULT_EXPIRATION_DAYS,
  LOCAL_STORAGE_KEY,
  SaveActionType,
  SaveErrors,
} from '~/core/constants';
import {
  addDaysToDate,
  getSearchParameter,
  gotoNewEstimate,
  handlePostError,
  setIsDirtyState,
} from '~/core/helpers';
import { appendLocalData, setLocalData } from '~/core/helpers/storageHelper';
import { useEstimate } from '~/core/hooks/useEstimate';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { useRouterTools } from '~/core/hooks/useRouterTools';
import { CustomerInfo, PricedProduct } from '~/core/models';
import { makeSaveEstimateFormData } from '~/core/models/save-estimate-dto';
import {
  generateCanvasImage,
  removeDataUrlPrefix,
  stringHash,
} from '~/core/utils';
import { NotifierType, showNotification } from '~/legacy/components';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  addCompletedEstimate,
  selectValidSmallTicketNumber,
  updateCurrentEstimate,
  updateIsNewEstimate,
  updateIsSavingEstimate,
  updateLoadEstimateCode,
  updateLoadEstimateKeys,
} from '~/redux/slices/estimate';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { lineItemsFromPricedProducts } from '~/redux/slices/pricedProducts/pricedProducts.utils';
import { usePricedProductsContext } from '~contexts';

interface SaveEstimateReturn {
  saveEstimate: (
    customerInfo: CustomerInfo,
    saveAction: SaveActionType
  ) => Promise<void>;
  isEstimateDirty: boolean;
  getDisplayDates: (alternateExpireDate?: Date) => {
    quoteDate: Date;
    expireDate: Date;
  };
}

/**
 * This is a string of the values from a product used to test for isDirty
 * @param product the pricedProduct record
 */
const dirtyProductString = (product: PricedProduct) =>
  [
    product.productId,
    product.name?.trim().toLowerCase(),
    product.quantity,
    product.adjustedQty,
    product.price,
    product.priceWithoutDiscount,
  ].join();

const useEstimateSaver = (): SaveEstimateReturn => {
  const { t } = useTranslation();

  const selectedStore = useSelectedStore();
  const [_saveEstimate] = useSaveEstimateMultiPartMutation();
  const { updateSearchParam } = useRouterTools();

  const { fetchedEstimate, resetLoadedFlags } = useEstimateContext();
  const { updateEstimateFromDesign } = useEstimate();

  /**
   * build a hash of a given pricedProducts list (either from the fetchedEstimate or current)
   *
   * @param products
   */
  const pricedProductHash = (products: PricedProduct[] | undefined): number => {
    if (!products?.length) {
      return 0;
    }

    return stringHash(
      products
        .map((product) => stringHash(dirtyProductString(product)))
        .sort()
        .join()
    );
  };

  const [fetchedProductsHash, setFetchedProductHash] = useState(() =>
    pricedProductHash(fetchedEstimate?.estimateData?.pricedProducts)
  );

  const canvas = useAppSelector(selectFabricCanvas);
  const { pricedProducts } = usePricedProductsContext();
  const dispatch = useAppDispatch();
  const { retailerSettings } = useRetailerSettings();

  const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);

  const testIsEstimateDirty = (): boolean => {
    const currentLineItems = lineItemsFromPricedProducts(pricedProducts);

    if (
      !currentLineItems.length &&
      !fetchedEstimate?.estimateData?.lineItems?.length
    ) {
      return false;
    }

    if (
      !fetchedEstimate?.estimateData ||
      fetchedEstimate?.storeId !== selectedStore?.companyId ||
      fetchedEstimate.estimateData.lineItems.length !== currentLineItems.length
    ) {
      return true;
    }

    if (fetchedEstimate.estimateData.pricedProducts?.length > 0) {
      return fetchedProductsHash !== pricedProductHash(pricedProducts);
    }

    // this is for older quotes that didn't have the pricedProducts saved, check lineItems
    const fetchedRecord: Record<number, number> =
      fetchedEstimate.estimateData.lineItems.reduce(
        (acc, item) => {
          acc[item.productId] = item.quantity;

          return acc;
        },
        {} as Record<number, number>
      );

    return currentLineItems.reduce((prev, item) => {
      const fetchedQuantity = fetchedRecord[item.productId];

      return prev || item.quantity !== fetchedQuantity;
    }, false);
  };

  /**
   * Return the dates that should be displayed when about to save a Quote.
   * - If it's a fetched estimate and no modifications have been made, return the dates from the estimate
   * - OR
   * - If it's been modified or it's new from scratch, use today's date and today+QUOTE_VALIDITY_IN_DAYS
   * - If given an alternateExpirationDate, use the minimum of this date or the computed dates, above
   *
   * **NOTE** EZE-582 was the source for this, but there's an issue with us not actually saving
   * the dates from the client, these are DISPLAY ONLY.
   * We may show one expiration date, but the back end calculates it again and it may be different from what we showed
   * @param alternateExpirationDate an optional alternative expiration date (generally from a promotion date)
   *     If we need to calculate new expirations, function will return the MINIMUM of the alternate or the default
   */
  const getDisplayDates = (
    alternateExpirationDate?: Date
  ): {
    quoteDate: Date;
    expireDate: Date;
  } => {
    const useFetchedQuoteDate =
      fetchedEstimate?.estimateData && fetchedEstimate?.quoteDate;
    const useFetchedExpireDate =
      fetchedEstimate &&
      !isEstimateDirty &&
      !!fetchedEstimate.quoteDate &&
      !!fetchedEstimate.quoteExpirationDate;
    const quoteDate = useFetchedQuoteDate
      ? new Date(fetchedEstimate.quoteDate)
      : new Date();
    const expirationDays = retailerSettings
      ? retailerSettings.expirationDays
      : DEFAULT_EXPIRATION_DAYS;
    const minExpireDate = new Date(
      Math.min(
        Number(alternateExpirationDate ?? Infinity),
        Number(addDaysToDate(quoteDate, expirationDays))
      )
    );
    const expireDate = useFetchedExpireDate
      ? new Date(fetchedEstimate.quoteExpirationDate)
      : minExpireDate;

    return { quoteDate, expireDate };
  };

  const saveEstimate = async (
    customerInfo: CustomerInfo,
    saveAction: SaveActionType
  ): Promise<void> => {
    dispatch(updateIsSavingEstimate(true));
    const estimate = updateEstimateFromDesign(customerInfo);

    dispatch(updateCurrentEstimate(estimate));

    const estimateImage = removeDataUrlPrefix(
      generateCanvasImage('png', canvas)
    );

    const lineItems = lineItemsFromPricedProducts(pricedProducts) || [];

    try {
      const productNote = validatedTicketNumber
        ? {
            productDescription: t('SMALL_PROJECTS.PRODUCT_NOTE.DESCRIPTION'),
            note: t('SMALL_PROJECTS.PRODUCT_NOTE.NOTE', {
              ticketNumber: validatedTicketNumber,
            }),
          }
        : null;

      const estimateData = productNote
        ? {
            ...estimate,
            productNotes: [...estimate.productNotes, productNote],
          }
        : estimate;

      const userToken = getSearchParameter(LOCAL_STORAGE_KEY.USER_TOKEN);

      const formData = makeSaveEstimateFormData({
        customer: customerInfo,
        estimateData,
        estimateImageBase64: estimateImage,
        lineItems,
        saveAction,
        code: fetchedEstimate?.code,
        ticketNumber: validatedTicketNumber,
        userToken,
      });

      const result = await _saveEstimate(formData).unwrap();

      if (result?.estimateNumber) {
        // TODO EE3-756
        //  this is the workaround to avoid state issues after saving a new estimate
        // see also useEstimateLoader

        // Add a notification that the estimate was saved to the NOTIFICATIONS local storage
        appendLocalData(LOCAL_STORAGE_KEY.NOTIFICATIONS, {
          estimateNumber: result.estimateNumber,
          notifierType: NotifierType.SUCCESS,
          title: 'Estimate Saved',
          description: 'This estimate has been saved successfully',
          message: `New Estimate Number: ${result.estimateNumber}`,
          duration: 0,
        });

        // just reload to the new estimate
        gotoNewEstimate(result.code, true);

        return;
        // End TODO EE3-756

        // TODO EE3-756 - this is blocked out because it forces a re-fech before reloading the page, which is not necessary
        //    I just put it in an if so it doesn't remove imports, etc.
        // TODO EE3-741 - put this back the way it was after fixing the underlying issues
        showNotification(
          NotifierType.SUCCESS,
          'Estimate Saved',
          'This estimate has been saved successfully',
          `New Estimate Number: ${result.estimateNumber}`,
          0
        );

        dispatch(updateIsNewEstimate(false));
        dispatch(
          addCompletedEstimate({
            code: result.code,
            isCompletedEstimate: true,
          })
        );

        setLocalData(LOCAL_STORAGE_KEY.LOAD_ESTIMATE, result.code);
        updateSearchParam(LOCAL_STORAGE_KEY.LOAD_ESTIMATE, result.code);
        dispatch(updateLoadEstimateCode(result.code));
        dispatch(updateLoadEstimateKeys(undefined));

        // Use setTimeout to ensure the clear happens first, then set new keys
        setTimeout(() => {
          dispatch(updateLoadEstimateKeys({ code: result.code }));
          resetLoadedFlags();
        }, 100);
      }

      return;

      throw new Error(SaveErrors.NO_ESTIMATE_NUMBER);
    } catch (error) {
      if (error instanceof AxiosError) {
        handlePostError(error);
      } else if (error instanceof Error) {
        // Handle generic errors
        console.error('An unexpected error occurred:', error.message);
        showNotification(
          NotifierType.ERROR,
          'Unexpected Error',
          'An unexpected error occurred',
          error.message,
          0
        );
      }
      throw error;
    } finally {
      dispatch(updateIsSavingEstimate(false));
    }
  };

  const isEstimateDirty = testIsEstimateDirty();

  useEffect(() => {
    if (!fetchedEstimate?.estimateData?.pricedProducts) {
      return;
    }
    setFetchedProductHash(
      pricedProductHash(fetchedEstimate.estimateData.pricedProducts)
    );
  }, [fetchedEstimate]);

  useEffect(() => {
    setIsDirtyState(isEstimateDirty);
  }, [isEstimateDirty]);

  return {
    saveEstimate,
    isEstimateDirty,
    getDisplayDates,
  };
};

export default useEstimateSaver;
